import { useQuery } from '@tanstack/react-query';
import { type CustomerAuthState } from '@hungryroot/customer';
import { getCustomerDietaryRestrictions } from '@hungryroot/rest/api/v2/customers/[id]/dietary_restrictions/get';
import { getCustomerQuizzes } from '@hungryroot/rest/api/v2/customers/[id]/quizzes/get';

type Props = {
  customer: CustomerAuthState;
};

type Options = {
  enabled?: boolean;
};

export function useRemoteQuiz({ customer }: Props, options: Options = {}) {
  const { enabled: isEnabled } = options;
  const customerId = customer.customerData?.id || -1;

  const query = useQuery({
    queryKey: ['customer', customerId, 'quizzes'],
    queryFn: async () => {
      const resp = await getCustomerQuizzes();

      const { status, data } = resp;

      if (status > 199 && status < 300) {
        return data;
      }

      throw data;
    },
    enabled: isEnabled !== false && customerId !== -1,
  });

  const { data, isLoading } = query;

  if (isLoading === true) {
    return {
      data: undefined,
    };
  }

  return {
    data,
  };
}

// Household size and eating habits are required quiz questions. This hook checks to make sure an autheticated user has completed these questions.
export function useRemoteQuizRequiredQuestions(
  { customer }: Props,
  options: Options = {}
) {
  const { enabled: isEnabled } = options;
  const customerId = customer.customerData?.id || -1;

  const { data: quizzesData } = useRemoteQuiz({ customer });

  const dietaryQuery = useQuery({
    queryKey: ['customer', customerId, 'dietary'],
    queryFn: async () => {
      const resp = await getCustomerDietaryRestrictions();

      const { status, data } = resp;

      if (status > 199 && status < 300) {
        return data;
      }

      throw data;
    },
    enabled:
      isEnabled !== false &&
      customerId !== -1 &&
      quizzesData?.household_size !== null &&
      quizzesData?.household_size !== undefined,
  });
  const { data: dietaryData } = dietaryQuery;

  if (quizzesData === undefined || dietaryData === undefined) {
    return undefined;
  }

  const eatingHabits = ['vegetarian', 'omnivore', 'pescatarian', 'vegan'];
  const customerHabits = dietaryData.dietary_restrictions.filter(({ slug }) =>
    eatingHabits.includes(slug)
  );

  if (
    quizzesData.household_size === null ||
    quizzesData.household_size === undefined ||
    customerHabits.length < 1
  ) {
    return undefined;
  }

  return true;
}
